import { useCallback, useEffect, useState } from 'react';
import { useScreenSize } from '@banhala/ui-kit';
import { type View } from 'react-native';
import {
  measure,
  useAnimatedRef,
  useFrameCallback,
  useSharedValue,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import { useIsForegroundNow } from 'shared/src/hooks';
import {
  retainedState,
  useRetainedState,
} from 'shared/src/hooks/retainedState';

const THROTTLE_MS = 50;
const MIN_VIEW_TIME_MS = 500;
// mount 게이트: 근접 상태가 이만큼 "안정적으로" 유지돼야 mount (초기/비동기 레이아웃 오판 방지).
const MIN_NEAR_MS = 50;
// 두 체크 사이 pageY 이동이 이보다 크면 "레이아웃 이동 중"으로 보고 dwell 리셋(px).
// (예: 상단 WebView 가 200→1556 으로 커지며 아래 섹션 pageY 가 흔들리는 구간)
const POSITION_STABLE_EPS = 4;

// 이번 visit 에 노출된 섹션 key 집합 (remount 가로질러 DISPLAY 재발화 방지).
const sectionVisibleState = retainedState<Set<string>>(() => new Set());

export const useIsSectionVisible = (key: string) => {
  const [visible] = useRetainedState(sectionVisibleState);
  return visible.has(key);
};

export const useMarkSectionVisible = () => {
  const [, setVisible] = useRetainedState(sectionVisibleState);
  return useCallback(
    (key: string) => {
      // 동일 참조 반환 시 useSyncExternalStore 가 리렌더 생략. 변경 시에만 새 Set.
      setVisible(prev => (prev.has(key) ? prev : new Set(prev).add(key)));
    },
    [setVisible],
  );
};

/**
 * 컴포넌트가 뷰포트에 진입했는지 감지하는 훅.
 * 중첩된 서브 섹션 컴포넌트에서 사용.
 *
 * 측정·dwell 판정을 전부 UI 스레드에서 수행한다(useFrameCallback + reanimated measure).
 * 연속 MIN_VIEW_TIME_MS 동안 가시 상태였으면 mark — "잠깐 스쳐 지나간" 케이스에서
 * 가짜 DISPLAY가 발화되는 것을 방지. 중간에 viewport를 벗어나면 visibleStart 리셋.
 * 100ms throttle, mark 이후 SharedValue 게이트로 즉시 early return.
 *
 * ⚠️ 메모리: viewRef 는 useAnimatedRef(=tag 기반 shareable)라 worklet 이 캡처해도
 * 컴포넌트(fiber)를 retain 하지 않는다. JS 로 넘기는 건 markVisible(setVisible)뿐이라
 * `__remoteFunctionRegistry` 가 트리를 붙들지 않는다.
 * (이전 구현은 `scheduleOnRN(checkVisibility)` 가 viewRef→View→fiber 를 캡처해
 *  PDP 트리 전체가 누수됐음 — 섹션 수만큼 ×N)
 */
export const useElementViewportTracking = (
  visibilityKey: string,
  offset = 0,
) => {
  const { height: screenHeight } = useScreenSize();
  const viewRef = useAnimatedRef<View>();
  const markVisible = useMarkSectionVisible();
  const isMarkedSV = useSharedValue(false);
  const visibleStartSV = useSharedValue(0);
  const lastCheckSV = useSharedValue(0);

  const frameCallback = useFrameCallback(frameInfo => {
    if (isMarkedSV.value) {
      return;
    }
    const now = frameInfo.timestamp;
    if (now - lastCheckSV.value < THROTTLE_MS) {
      return;
    }
    lastCheckSV.value = now;
    // 뷰가 아직 레이아웃 안 된 프레임엔 native measure 가 throw 할 수 있어 가드.
    // (worklet 에선 animatedRef.current 가 신뢰 불가 → try/catch 로 방어)
    let measured: ReturnType<typeof measure> = null;
    try {
      measured = measure(viewRef);
    } catch (_error) {
      return;
    }
    if (measured === null) {
      return;
    }
    const isInViewport =
      measured.height > 0 &&
      measured.pageY + measured.height > 0 &&
      measured.pageY < screenHeight - offset;

    if (!isInViewport) {
      visibleStartSV.value = 0;
      return;
    }
    if (visibleStartSV.value === 0) {
      visibleStartSV.value = now;
    } else if (now - visibleStartSV.value >= MIN_VIEW_TIME_MS) {
      isMarkedSV.value = true;
      scheduleOnRN(markVisible, visibilityKey);
    }
  });

  // 화면이 가려지면(다른 화면 push / 백그라운드) frame callback 을 멈춘다.
  // 가려진 동안 measure()가 not-rendered 뷰를 계속 측정하면 reanimated 가 undefined
  // LayoutMetrics 경고를 쏟다가 끝내 libworklets 네이티브 assert(getObject)로 크래시한다.
  // (JS try/catch 는 네이티브 abort 를 못 막는다.) 노출 판정은 화면이 보일 때만 의미 있으므로
  // 가려진 동안 비활성화해도 무방하고, CPU(매 프레임 measure)도 아낀다.
  const isForeground = useIsForegroundNow();
  useEffect(() => {
    frameCallback.setActive(isForeground);
  }, [frameCallback, isForeground]);

  return viewRef;
};

/**
 * 뷰포트에 "곧 진입"하면 mount 시키기 위한 훅 (지연 mount 게이트).
 *
 * `screensAhead` 만큼 뷰포트 아래(runway)를 미리 포함해 **화면에 닿기 전에 mount**한다
 * (스크롤 도달 순간 mount → blank/프레임 드랍 방지). DISPLAY 로깅의 500ms dwell 보다 짧은
 * MIN_NEAR_MS 확인시간 + 위치안정성(pageY 이동 감지)으로, 초기/비동기 레이아웃(상단 WebView
 * 성장 등)에서의 첫 프레임 오판만 거르고 진짜 근접 시엔 사실상 즉시 mount 한다.
 * 한 번 mount 되면 유지(persist)하고 frame callback 을 중단해 CPU 를 아낀다(재mount/thrash 방지).
 *
 * 반환한 ref 는 자리표시(placeholder) 요소에 달아 위치를 측정한다(높이 0 이어도 pageY 로 판정).
 * leak 안전성은 useElementViewportTracking 과 동일(scheduleOnRN 은 setState 만 캡처).
 */
export const useMountWhenNearViewport = (
  screensAhead = 1.5,
): readonly [ReturnType<typeof useAnimatedRef<View>>, boolean] => {
  const { height: screenHeight } = useScreenSize();
  const viewRef = useAnimatedRef<View>();
  const [mounted, setMounted] = useState(false);
  const doneSV = useSharedValue(false);
  const lastCheckSV = useSharedValue(0);
  const nearStartSV = useSharedValue(0);
  const lastYSV = useSharedValue(Number.NaN);

  const frameCallback = useFrameCallback(frameInfo => {
    if (doneSV.value) {
      return;
    }
    const now = frameInfo.timestamp;
    if (now - lastCheckSV.value < THROTTLE_MS) {
      return;
    }
    lastCheckSV.value = now;
    let measured: ReturnType<typeof measure> = null;
    try {
      measured = measure(viewRef);
    } catch (_error) {
      return;
    }
    if (measured === null) {
      return;
    }
    const runway = screenHeight * screensAhead;
    // 뷰포트 ± runway 안에 걸치면 "곧 진입"으로 본다 (placeholder 높이 0 도 pageY 로 판정).
    const near =
      measured.pageY + measured.height > -runway &&
      measured.pageY < screenHeight + runway;
    // 직전 체크 대비 pageY 가 크게 움직였으면(상단 WebView 성장 등) 레이아웃 이동 중 → 오판 방지.
    const moving =
      !Number.isNaN(lastYSV.value) &&
      Math.abs(measured.pageY - lastYSV.value) > POSITION_STABLE_EPS;
    lastYSV.value = measured.pageY;

    if (!near || moving) {
      nearStartSV.value = 0; // 근접 아님 / 이동 중 → dwell 리셋
      return;
    }
    if (nearStartSV.value === 0) {
      nearStartSV.value = now;
    } else if (now - nearStartSV.value >= MIN_NEAR_MS) {
      doneSV.value = true;
      scheduleOnRN(setMounted, true);
    }
  });

  const isForeground = useIsForegroundNow();
  useEffect(() => {
    // mount 확정 후엔 콜백 중단(persist + CPU 절약). 가려진 동안도 중단.
    frameCallback.setActive(isForeground && !mounted);
  }, [frameCallback, isForeground, mounted]);

  return [viewRef, mounted] as const;
};
